/**
 * customer-app · 全局命名空间根 `window.OS`（OS 总线本体）
 *
 * 来源：js/global-store.js（已迁移）。
 *
 * 设计要点（必须保留）：
 *   1. `OS.modules` 由各 lib 自报家门 (OS.registerModule(name, ref))；重复注册告警
 *   2. `OS.state` 提供 get/set/update/subscribe，替代 lib 内部闭包变量，便于 trace
 *   3. `OS.events` 是简单的 EventTarget-like 总线，给跨模块解耦用
 *   4. `OS.debug.{listGlobals, trace, snapshot}` 在生产保留，方便顾客 iPad 远程排错
 *   5. 不主动覆盖 `window.RestaurantXxx`：各 lib 仍按原方式 `global.RestaurantXxx = {...}`，
 *      `OS.registerModule` 只是侧通道；这样完全增量、无破坏
 *   6. **必须**在 customer-app-entry.js 第一条加载（在 app-receipt-data.js 之前）
 *
 * 兼容契约：
 *   - 顶层属性全部用 `Object.defineProperty` 注册，并 `Object.freeze(root)` 锁定
 *     → 防止业务误操作；运行时行为与原 IIFE 完全一致
 *   - `OS.modules` 是 Proxy，拒绝直接赋值/删除并打印警告
 *   - `_modules` / `_state` / `_watchers` 用 `Object.create(null)` 防原型污染
 *
 * 设计：
 *   - 类型层只用局部 `RuntimeGlobal` 断言；**不修改全局 Window.OS 声明**
 *     （避免与 customer-bootstrap/types.ts 中已有的 `OrdersystemFacade` 全局类型冲突）
 *   - `ModuleName` / `StateKey` / `EventName` 全部保留 `string` 开放契约
 *     → admin/customer-lite/未来扩展模块可任意起名
 */

/* ─────────── 公共类型 ─────────── */

export type ModuleName = string;
export type ModuleRef = unknown;
export type StateKey = string;
export type StateValue = unknown;
export type EventName = string;
export type EventPayload = unknown;

export type Unsubscribe = () => void;

/** state.subscribe 的回调签名 */
export type StateSubscriber<T = unknown> = (value: T, prev: T | undefined) => void;
/** events.on 的回调签名 */
export type EventListener<T = unknown> = (payload: T) => void;

export interface OsEventsApi {
  on<T = EventPayload>(evt: EventName, cb: EventListener<T>): Unsubscribe;
  off<T = EventPayload>(evt: EventName, cb: EventListener<T>): void;
  emit<T = EventPayload>(evt: EventName, payload?: T): void;
}

export interface OsStateApi {
  get<T = StateValue>(key: StateKey): T | undefined;
  set<T = StateValue>(key: StateKey, value: T, opts?: { force?: boolean }): void;
  update<T = StateValue>(key: StateKey, fn: (prev: T | undefined) => T): void;
  subscribe<T = StateValue>(key: StateKey, cb: StateSubscriber<T>): Unsubscribe;
  keys(): string[];
}

export interface OsModuleSnapshotRow {
  module: string;
  type: string;
  fields: number;
}

export interface OsDebugApi {
  listGlobals(): OsModuleSnapshotRow[];
  trace(key: StateKey, on?: boolean): Unsubscribe | null;
  snapshot(): {
    version?: string;
    ready?: boolean;
    modules?: string[];
    state?: Record<string, unknown>;
    error?: string;
  };
}

/** OS 根对象总接口（与 customer-bootstrap 中的 OrdersystemFacade 是不同概念：
 *  本接口描述「总线本体」，OrdersystemFacade 描述业务 ui 层注册后的扩展面） */
export interface OrdersystemRoot {
  readonly version: string;
  ready: boolean;
  /** 只读视图：实际拦截写入的 Proxy（修改请用 registerModule） */
  readonly modules: Record<string, ModuleRef>;
  readonly state: OsStateApi;
  readonly events: OsEventsApi;
  readonly debug: OsDebugApi;
  registerModule<T extends ModuleRef = ModuleRef>(name: ModuleName, ref: T): T;
  facade: ModuleRef | null;
  /**
   * 启动早期由 customer-bootstrap 写入（如 notifyTableOccupied）；
   * 初始化 OS 总线时从占位 `window.OS` 合并，避免被 `g.OS = root` 覆盖丢失。
   */
  readonly hooks: Record<string, unknown>;
}

/* ─────────── 全局 facade 局部断言 ─────────── */

interface RuntimeGlobal {
  /**
   * 注：此处刻意用 `unknown` 而不是 `OrdersystemRoot`，避免与
   * customer-bootstrap/types.ts 中 `Window.OS: OrdersystemFacade` 全局声明冲突。
   * 写入时通过断言一次到位。
   */
  OS?: unknown;
  APP_VERSION?: string;
  console?: Console;
}

const g = (typeof globalThis !== 'undefined' ? globalThis : window) as unknown as RuntimeGlobal;

/* ─────────── 单次初始化守卫 ─────────── */

/** 已初始化标记隐藏在非枚举字段上；外部检测靠 `OS.__INITIALIZED__ === true` */
const existing = g.OS as { __INITIALIZED__?: true; hooks?: Record<string, unknown> } | undefined;

/** customer-bootstrap 可能在 global-store 之前写入 `window.OS.hooks.notifyTableOccupied` */
const preBootstrapHooks: Record<string, unknown> =
  existing &&
  !existing.__INITIALIZED__ &&
  existing.hooks &&
  typeof existing.hooks === 'object' &&
  existing.hooks !== null &&
  !Array.isArray(existing.hooks)
    ? Object.assign(Object.create(null), existing.hooks as Record<string, unknown>)
    : Object.create(null);

if (!(existing && existing.__INITIALIZED__)) {
  /* ─────────── 内部存储（防原型污染） ─────────── */

  const _modules: Record<string, ModuleRef> = Object.create(null);
  const _state: Record<string, StateValue> = Object.create(null);
  const _watchers: Record<string, Set<StateSubscriber>> = Object.create(null);
  let _facade: ModuleRef | null = null;
  let _ready = false;

  /* ─────────── 事件总线 ─────────── */

  const _eventMap: Map<string, Set<EventListener>> = new Map();
  const events: OsEventsApi = {
    on(evt, cb) {
      if (typeof cb !== 'function') return () => {};
      let set = _eventMap.get(evt);
      if (!set) {
        set = new Set();
        _eventMap.set(evt, set);
      }
      const wrapped = cb as EventListener;
      set.add(wrapped);
      return () => {
        set!.delete(wrapped);
      };
    },
    off(evt, cb) {
      const set = _eventMap.get(evt);
      if (set) set.delete(cb as EventListener);
    },
    emit(evt, payload) {
      const set = _eventMap.get(evt);
      if (!set) return;
      set.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          if (g.console) g.console.error('[OS.events]', evt, e);
        }
      });
    },
  };

  /* ─────────── 模块注册 ─────────── */

  const registerModule = <T extends ModuleRef = ModuleRef>(name: ModuleName, ref: T): T => {
    const key = String(name ?? '').trim();
    if (!key) {
      if (g.console) g.console.warn('[OS.modules] registerModule 缺少名字');
      return ref;
    }
    if (_modules[key]) {
      if (g.console) {
        g.console.warn(`[OS.modules] "${key}" 已注册，新值被忽略；检查是否重复加载脚本`);
      }
      return _modules[key] as T;
    }
    _modules[key] = ref;
    events.emit('module:registered', { name: key, ref });
    return ref;
  };

  /* ─────────── 响应式状态 ─────────── */

  const stateApi: OsStateApi = {
    get<T = StateValue>(key: StateKey): T | undefined {
      return _state[key] as T | undefined;
    },
    set<T = StateValue>(key: StateKey, value: T, opts?: { force?: boolean }): void {
      const prev = _state[key];
      if (prev === value && !(opts && opts.force)) return;
      _state[key] = value;
      const ws = _watchers[key];
      if (ws) {
        ws.forEach((cb) => {
          try {
            (cb as StateSubscriber<T>)(value, prev as T | undefined);
          } catch (e) {
            if (g.console) g.console.error('[OS.state]', key, e);
          }
        });
      }
      events.emit('state:change', { key, value, prev });
    },
    update<T = StateValue>(key: StateKey, fn: (prev: T | undefined) => T): void {
      if (typeof fn !== 'function') return;
      stateApi.set(key, fn(_state[key] as T | undefined));
    },
    subscribe<T = StateValue>(key: StateKey, cb: StateSubscriber<T>): Unsubscribe {
      if (typeof cb !== 'function') return () => {};
      let set = _watchers[key];
      if (!set) {
        set = new Set();
        _watchers[key] = set;
      }
      const wrapped = cb as StateSubscriber;
      set.add(wrapped);
      return () => {
        set!.delete(wrapped);
      };
    },
    keys(): string[] {
      return Object.keys(_state);
    },
  };

  /* ─────────── 调试 ─────────── */

  const debugApi: OsDebugApi = {
    listGlobals(): OsModuleSnapshotRow[] {
      const rows: OsModuleSnapshotRow[] = Object.keys(_modules).map((k) => {
        const ref = _modules[k];
        return {
          module: k,
          type: ref == null ? 'null' : typeof ref,
          fields: ref && typeof ref === 'object' ? Object.keys(ref).length : 0,
        };
      });
      if (g.console && g.console.table) g.console.table(rows);
      else if (g.console) g.console.log(rows);
      return rows;
    },
    trace(key: StateKey, on?: boolean): Unsubscribe | null {
      const enabled = on !== false;
      if (!enabled) return null;
      return stateApi.subscribe(key, (v, p) => {
        if (g.console) g.console.log(`[OS.trace] ${key}`, p, '→', v);
      });
    },
    snapshot() {
      try {
        return JSON.parse(
          JSON.stringify({
            version: g.APP_VERSION || '0.0.0',
            ready: _ready,
            modules: Object.keys(_modules),
            state: _state,
          }),
        );
      } catch {
        return { error: 'snapshot serialize failed' };
      }
    },
  };

  /* ─────────── 根对象（不可写顶层属性 + freeze） ─────────── */

  /** 用 `as unknown as OrdersystemRoot` 是因为运行时由 defineProperty 逐字段补齐，
   *  TS 看不到这一点；二跳断言比逐字段类型更紧。 */
  const root = {} as unknown as OrdersystemRoot & { __INITIALIZED__?: true };

  Object.defineProperty(root, '__INITIALIZED__', { value: true, enumerable: false });
  Object.defineProperty(root, 'version', {
    enumerable: true,
    get: () => g.APP_VERSION || '0.0.0',
  });
  Object.defineProperty(root, 'ready', {
    enumerable: true,
    get: () => _ready,
    set: (v: unknown) => {
      _ready = !!v;
      events.emit('ready', _ready);
    },
  });
  Object.defineProperty(root, 'modules', {
    enumerable: true,
    value: new Proxy(_modules, {
      set: (_t, k) => {
        if (g.console) {
          g.console.warn(
            `[OS.modules] 拒绝直接赋值 OS.modules.${String(k)}；请用 OS.registerModule(name, ref)`,
          );
        }
        return true;
      },
      deleteProperty: () => {
        if (g.console) g.console.warn('[OS.modules] 拒绝 delete');
        return true;
      },
    }),
  });
  Object.defineProperty(root, 'state', { value: stateApi, enumerable: true });
  Object.defineProperty(root, 'events', { value: events, enumerable: true });
  Object.defineProperty(root, 'debug', { value: debugApi, enumerable: true });
  Object.defineProperty(root, 'registerModule', { value: registerModule, enumerable: true });
  Object.defineProperty(root, 'facade', {
    enumerable: true,
    get: () => _facade,
    set: (v: unknown) => {
      if (_facade) {
        if (g.console) g.console.warn('[OS.facade] 已注册，忽略新值');
        return;
      }
      _facade = v as ModuleRef;
      events.emit('facade:ready', v);
    },
  });
  Object.defineProperty(root, 'hooks', { enumerable: true, value: preBootstrapHooks });

  Object.freeze(root);
  g.OS = root;
}
